import React, { createContext, useState, useCallback, useEffect } from 'react';
import { workspacesApi } from '../../api/namespaces/workspaces';
import { useAuth } from '../auth/useAuth';
import { User, Workspace, WorkspaceAccessLevel } from '../../types';

interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  setCurrentWorkspace: (workspace: Workspace | null) => void;
  workspaces: Workspace[];
  refreshWorkspaces: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  currentUserAccess: WorkspaceAccessLevel | null;
}

export const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth() as { user: User | null };
  const [currentWorkspace, _setCurrentWorkspace] = useState<Workspace | null>(null);

  const setCurrentWorkspace = useCallback((workspace: Workspace | null) => {
    _setCurrentWorkspace(workspace);
    if (workspace) {
      localStorage.setItem('currentWorkspaceId', workspace.id.toString());
    } else {
      localStorage.removeItem('currentWorkspaceId');
    }
  }, []);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compute current user's access level in the current workspace
  const currentUserAccess = currentWorkspace
    ? currentWorkspace.members.find(m => m.user.id === user?.id)?.access_level || null
    : null;

  const refreshWorkspaces = useCallback(async () => {
    if (!user) return;
    try {
      setIsLoading(true);
      setError(null);
      const workspaceList = await workspacesApi.getAll();
      setWorkspaces(workspaceList);
      
      // Handle workspace selection
      const savedId = localStorage.getItem('currentWorkspaceId');
      if (savedId) {
        const savedWorkspace = workspaceList.find((w: Workspace) => w.id === parseInt(savedId));
        if (savedWorkspace) {
          _setCurrentWorkspace(savedWorkspace);
        } else {
          // If saved workspace not found, clear it
          localStorage.removeItem('currentWorkspaceId');
          _setCurrentWorkspace(null);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workspaces');
    } finally {
      setIsLoading(false);
    }
  }, [user, _setCurrentWorkspace]);

  // Load workspaces on mount and when user changes
  useEffect(() => {
    if (user) {
      refreshWorkspaces();
    } else {
      setWorkspaces([]);
      setCurrentWorkspace(null);
    }
  }, [user, refreshWorkspaces, setCurrentWorkspace]);

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspace,
        setCurrentWorkspace,
        workspaces,
        refreshWorkspaces,
        isLoading,
        error,
        currentUserAccess,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
